// ─── Guided Setup: shop analytics ─────────────────────────────────────────────
//
// detector_config.video_analytics, per camera. Draw the door and the zones, pick
// the trading hours and the hour to run, and the shop report refreshes itself
// nightly from the recordings the site already keeps.
//
// Why this one is scheduled and every other module is live: measured, the
// analyzer runs at 1.24x realtime (1.96x with shelves) at the 8 fps the counting
// needs to be correct, so nothing reading a live stream can keep up. The panel
// says that in the customer's terms — "ready by morning" — rather than leaving
// them to discover it from an empty report at lunchtime.
//
// It also refuses to be vague about the two things that decide whether the
// numbers are right at all: the line must be across the doorway, and recording
// must be on for the hours being counted. Both are stated here, not buried in a
// hint, because neither is recoverable after the fact — footage that was never
// recorded cannot be analysed later.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import PatrolZoneEditor from '../PatrolZoneEditor';
import { Field, Panel, SettingRow, Advanced, countChanged, PolygonList,
         DayPicker, useModuleSaver } from './SetupBits';

const DETECTOR_KEY = 'video_analytics';

// Defaults mirror video_schedule.DEFAULTS and video_analytics.DEFAULT_PARAMS.
// They must stay in step: a value invented here would be silently replaced by
// the backend's own default and the panel would be showing a number that is not
// in force.
const ADVANCED = [
  { key: 'analysis_fps', label: 'Frames examined per second', type: 'int',
    min: 3, max: 8, step: 1, def: 8,
    hint: 'Leave at 8. Lower is faster but MISCOUNTS — measured on real shop footage, 3 fps reported 2 customers in and 1 out where two came in and two left, because a track drifts onto another person between frames.' },
  { key: 'min_visit_secs', label: 'Ignore visits shorter than', type: 'int',
    min: 1, max: 30, step: 1, def: 3,
    hint: 'Somebody who appears for a moment is noise, not a customer.' },
  { key: 'staff_min_secs', label: 'Seconds behind the counter to count as staff', type: 'int',
    min: 1, max: 30, step: 1, def: 3,
    hint: 'Anyone who spends this long on the staff side is staff for the whole window and never counts as a customer.' },
];

const RETENTION = [
  { key: 'report_retention_days', label: 'Keep the numbers for', type: 'int',
    min: 7, max: 400, step: 1, def: 90, hint: 'Report rows are a few KB a day.' },
  { key: 'proof_retention_days', label: 'Keep the proof photos for', type: 'int',
    min: 1, max: 400, step: 1, def: 20,
    hint: 'The photo behind each figure. Deleted on this clock — the numbers above outlive them.' },
];

const blank = (idx) => ({
  idx,
  enabled: true,
  window: { start: '10:00', end: '13:00' },
  run_at: '21:00',
  days: [0, 1, 2, 3, 4, 5, 6],
  report_retention_days: 90,
  proof_retention_days: 20,
  keep_video: false,
  params: { analysis_fps: 8, min_visit_secs: 3, staff_min_secs: 3, detect_shelf: true,
            entry_line: null, shop_zone: null, counter_zone: null, service_zone: null,
            staff_zones: [], shelf_zones: [] },
});

// Hours of footage -> hours of computing, from the measured factors. Shown
// because a customer who asks for all ten trading hours needs to see that it
// cannot finish before the shop opens again, while the choice is still theirs.
function costOf(cfg) {
  const [sh, sm] = (cfg.window?.start || '0:0').split(':').map(Number);
  const [eh, em] = (cfg.window?.end || '0:0').split(':').map(Number);
  let hrs = (eh + em / 60) - (sh + sm / 60);
  if (hrs <= 0) hrs += 24;
  const shelves = (cfg.params?.shelf_zones || []).length > 0;
  return { hrs, cost: hrs * (shelves ? 1.96 : 1.24), shelves };
}

export default function ShopAnalyticsPanel({ cameras = [], onChanged }) {
  const [rows, setRows] = useState([]);       // configured cameras
  const [edit, setEdit] = useState(null);
  const { busy, err, note, run } = useModuleSaver(apiFetch, onChanged);

  const reload = useCallback(async () => {
    try {
      const r = await apiFetch('/ui/cameras');
      const d = await r.json();
      setRows((d.cameras || [])
        .filter(c => (c.detector_config || {})[DETECTOR_KEY])
        .map(c => ({ idx: c.idx, name: c.name || `Camera ${c.idx}`,
                     cfg: c.detector_config[DETECTOR_KEY] })));
    } catch { /* the panel still works for adding a new camera */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const open = (idx) => {
    const found = rows.find(r => r.idx === idx);
    const b = blank(idx);
    const s = found?.cfg || {};
    setEdit({
      ...b, ...s, idx,
      window: { ...b.window, ...(s.window || {}) },
      days: Array.isArray(s.days) && s.days.length ? s.days : b.days,
      params: { ...b.params, ...(s.params || {}),
                staff_zones: Array.isArray(s.params?.staff_zones) ? s.params.staff_zones : [],
                shelf_zones: Array.isArray(s.params?.shelf_zones) ? s.params.shelf_zones : [] },
    });
  };

  const patch = (p) => setEdit(e => ({ ...e, ...p }));
  const patchParam = (p) => setEdit(e => ({ ...e, params: { ...e.params, ...p } }));

  const save = async () => {
    const { idx, ...cfg } = edit;
    const P = cfg.params || {};
    const drawn = P.entry_line || P.shop_zone || P.counter_zone || P.service_zone
                  || (P.staff_zones || []).length || (P.shelf_zones || []).length;
    if (!drawn) return;                       // the button is disabled; belt and braces
    const out = await run('PUT', `/cameras/${idx}`,
                          { detector_config: { [DETECTOR_KEY]: cfg } },
                          'the shop analytics setup');
    if (out) { setEdit(null); await reload(); }
  };

  // ── list ───────────────────────────────────────────────────────────────────
  if (!edit) {
    return (
      <>
        <Panel tone="info" title="The report is ready by morning, not live">
          This is the only module that is not instant. Working out how long each
          customer stayed takes more computing than the footage takes to play, so
          it runs after the shop shuts and the report is waiting the next day.
          Live people-in and people-out counting is a different module — set that
          up under “Counting and footfall”.
        </Panel>

        {rows.map(r => {
          const { hrs, cost } = costOf(r.cfg || {});
          return (
            <div className="gs-row" key={r.idx}>
              <span className="gs-row-icon">🛒</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{r.name}</span>
                <span className="gs-row-sub">
                  {r.cfg?.enabled === false
                    ? 'switched off'
                    : `${r.cfg?.window?.start}–${r.cfg?.window?.end}, analysed at ${r.cfg?.run_at}`}
                  {' · '}{hrs.toFixed(1)} h of footage, about {cost.toFixed(1)} h to work through
                </span>
              </span>
              <button className="gs-btn small ghost" onClick={() => open(r.idx)}>Change</button>
            </div>
          );
        })}
        {!rows.length && <p className="gs-foot-note">No camera set up for shop analytics yet.</p>}

        <Field label="Set up a camera">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {cameras.map(c => (
              <button key={c.idx} className="gs-chip" onClick={() => open(c.idx)}>
                {c.name || `Camera ${c.idx}`}
              </button>
            ))}
            {!cameras.length && <span className="gs-foot-note">Add a camera first.</span>}
          </div>
        </Field>
        {err && <Panel tone="bad" title="Could not save">{err}</Panel>}
        {note && <Panel tone="good" title="Saved">{note}</Panel>}
      </>
    );
  }

  // ── editor ─────────────────────────────────────────────────────────────────
  const P = edit.params || {};
  const { hrs, cost, shelves } = costOf(edit);
  const drawn = !!(P.entry_line || P.shop_zone || P.counter_zone || P.service_zone
                   || (P.staff_zones || []).length || (P.shelf_zones || []).length);
  const cam = cameras.find(c => c.idx === edit.idx);

  return (
    <>
      <Panel tone="warn" title="Two things decide whether the numbers are right">
        <b>Put the counting line across the doorway itself.</b> Drawn a step inside
        the shop it misses people arriving, and the report then says one customer
        came in on footage where two did.
        <br /><br />
        <b>Recording must be on for the hours below.</b> The report is built from
        the recordings, so footage that was never recorded cannot be analysed
        afterwards. Three hours a day is about 2 GB.
      </Panel>

      <Field label="Camera">
        <span className="gs-row-name">{cam?.name || `Camera ${edit.idx}`}</span>
      </Field>

      <Field label="The doorway"
             help="Two clicks, across the door. People crossing it are counted in and out.">
        <PatrolZoneEditor cameraIdx={edit.idx} shape="line"
                          value={P.entry_line || []}
                          onChange={p => patchParam({ entry_line: p.length === 2 ? p : null })} />
      </Field>

      <Field label="The shop floor"
             help="Time spent is measured inside this area, which keeps people passing the door out of the figures. One area; adding another replaces it.">
        <PolygonList cameraIdx={edit.idx} noun="shop floor"
                     value={P.shop_zone ? [P.shop_zone] : []}
                     onChange={v => patchParam({ shop_zone: v.length ? v[v.length - 1] : null })} />
      </Field>

      <Field label="Behind the counter"
             help="The staff side. Anyone who spends a few seconds here is staff for the whole window and is never counted as a customer, and the time each post was manned is reported. Usually the only staff area needed.">
        <PolygonList cameraIdx={edit.idx} noun="counter"
                     value={P.counter_zone ? [P.counter_zone] : []}
                     onChange={v => patchParam({ counter_zone: v.length ? v[v.length - 1] : null })} />
      </Field>

      <Field label="Where customers stand to be served"
             help="The customer side of the counter. Gives time spent at the counter and how many people were served.">
        <PolygonList cameraIdx={edit.idx} noun="service area"
                     value={P.service_zone ? [P.service_zone] : []}
                     onChange={v => patchParam({ service_zone: v.length ? v[v.length - 1] : null })} />
      </Field>

      <Advanced title="Other areas" changed={(P.staff_zones || []).length + (P.shelf_zones || []).length}>
        <Field label="Other staff areas"
               help="Only for places staff go that are NOT the counter — a back room or a dispensary. Do not draw one over floor a customer can stand on, or that customer becomes staff.">
          <PolygonList cameraIdx={edit.idx} noun="staff area"
                       value={P.staff_zones || []}
                       onChange={v => patchParam({ staff_zones: v })} />
        </Field>
        <Field label="Shelves and drawers"
               help="Counts reaches, not openings — there is no sensor on the door, so it reads a hand entering or the contents changing. Draw it tight to the shelf face. Each shelf adds about 35% to the time the analysis takes.">
          <PolygonList cameraIdx={edit.idx} noun="shelf" named
                       value={P.shelf_zones || []}
                       onChange={v => patchParam({ shelf_zones: v })} />
        </Field>
      </Advanced>

      <Field label="Trading hours to count"
             help="The stretch of each day to analyse. An overnight span such as 22:00 to 02:00 is understood.">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="gs-input" type="time" value={edit.window.start}
                 onChange={e => patch({ window: { ...edit.window, start: e.target.value } })} />
          <span className="gs-foot-note">to</span>
          <input className="gs-input" type="time" value={edit.window.end}
                 onChange={e => patch({ window: { ...edit.window, end: e.target.value } })} />
        </div>
      </Field>

      <Field label="Work through it at"
             help="Kept separate from the hours above on purpose: doing this while the shop is open would take the computing power the live cameras need. After closing is right.">
        <input className="gs-input" type="time" value={edit.run_at}
               onChange={e => patch({ run_at: e.target.value })} />
      </Field>

      <Field label="Which days">
        <DayPicker value={edit.days} onChange={v => patch({ days: v })} />
      </Field>

      <Panel tone={cost > 10 ? 'warn' : 'info'} title="How long it will take">
        {hrs.toFixed(1)} hours of footage{shelves ? ', with shelves to check,' : ''} takes
        roughly <b>{cost.toFixed(1)} hours</b> to work through. Starting
        at {edit.run_at}, the report should be ready
        around {(() => {
          const [h, m] = edit.run_at.split(':').map(Number);
          const end = (h + cost) % 24;
          return `${String(Math.floor(end)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        })()}.
        {cost > 10 && <> That is too long — it will not be finished before the next
          day’s window opens. Shorten the hours, or leave the shelves out.</>}
      </Panel>

      <Advanced title="Fine tuning" changed={countChanged(ADVANCED, P)}>
        {ADVANCED.map(spec => (
          <SettingRow key={spec.key} spec={spec} value={P[spec.key]}
                      onChange={v => patchParam({ [spec.key]: v })} />
        ))}
      </Advanced>

      <Advanced title="How long to keep the report" changed={countChanged(RETENTION, edit)}>
        {RETENTION.map(spec => (
          <SettingRow key={spec.key} spec={spec} value={edit[spec.key]}
                      onChange={v => patch({ [spec.key]: v })} />
        ))}
      </Advanced>

      <SettingRow
        spec={{ key: 'enabled', label: 'Analyse this camera', type: 'bool', def: true,
                hint: 'Turn off to keep the setup but stop the nightly run.' }}
        value={edit.enabled} onChange={v => patch({ enabled: v })} />

      {err && <Panel tone="bad" title="Could not save">{err}</Panel>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <button className="gs-btn" onClick={save} disabled={busy || !drawn}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="gs-btn ghost" onClick={() => setEdit(null)} disabled={busy}>
          Cancel
        </button>
        {!drawn && <span className="gs-foot-note">
          Draw the doorway, or at least one area, before saving — with nothing drawn
          there is nothing to measure.
        </span>}
      </div>
    </>
  );
}
